import type { CSSProperties, ReactNode, Ref } from 'react';

import { cn } from '@ps/ui/utils';

interface Props {
  /** Computed button style (bg, border, radius, height/min-height/aspect). */
  buttonStyle: CSSProperties;
  /** Per-renderer style override layered last (e.g. a video aspect ratio). */
  style?: CSSProperties;
  className?: string;
  children: ReactNode;
  /**
   * Optional outer-element ref. Used by the magic-video island to drive an
   * IntersectionObserver against the container. A pure prop — passing it is the
   * caller's choice; the container itself stays server-renderable.
   */
  containerRef?: Ref<HTMLDivElement>;
}

/**
 * The styled outer box of a custom button — a pure presentational wrapper.
 * Server-renderable (no hooks/context); it just paints `buttonStyle` onto a
 * `relative flex-col overflow-hidden` div and renders its children.
 *
 * The explicit zero/transparent border baseline neutralises the host's
 * `* { @apply border-border }` global reset, so a border paints ONLY when the
 * creator's `buttonStyle.border*` (or a per-renderer override) carries one.
 */
export function CustomButtonContainer({
  buttonStyle,
  style,
  className,
  children,
  containerRef,
}: Props) {
  return (
    <div
      ref={containerRef}
      className={cn(
        'relative flex h-full w-full flex-col overflow-hidden',
        className,
      )}
      style={{
        borderWidth: 0,
        borderStyle: 'solid',
        borderColor: 'transparent',
        ...buttonStyle,
        ...style,
        // Per-renderer minHeight override: when `style.minHeight` is the
        // EXPLICIT `undefined` (a renderer nuking the wrapper default so an
        // aspect-ratio can drive height), respect it. When `style` doesn't
        // carry the key at all, fall back to the wrapper's default.
        minHeight:
          style && 'minHeight' in style
            ? style.minHeight
            : buttonStyle.minHeight,
      }}
    >
      {children}
    </div>
  );
}
